<template>
  <div
    v-clipboard:copy="copy"
    v-clipboard:onSuccess="handleSuccess"
    v-clipboard:onError="handleError"
    :class="`sb-clipboard sb-clipboard--${state}`"
    data-sb-regression="sb-clipboard"
  >
    <SbPopover
      :target="$el"
      :active="state === 'success' || state === 'error'"
      :position-fixed="true"
      placement="center"
      boundaries-element="viewport"
      :offset="{ orientation: '5px', placement: '' }"
    >
      <FadeTransition>
        <SbPanel
          v-if="state === 'success' || state === 'error'"
          variation="success"
          class="sb-clipboard__messaging-block"
        >
          <div
            v-if="state === 'success'"
            class="sb-clipboard__messaging"
          >
            <!--
              @slot slot to show succes message
            -->
            <slot name="success">
              <sb-text :value="successMessage" />
            </slot>
          </div>
          <div
            v-if="state === 'error'"
            class="sb-clipboard__messaging"
          >
            <!--
              @slot slot to show error message
            -->
            <slot name="error">
              <sb-text :value="errorMessage" />
            </slot>
          </div>
        </SbPanel>
      </FadeTransition>
    </SbPopover>
    <span @click="invokeCopy">
      <slot>
        <i
          ref="clipboard-icon"
          aria-hidden="true"
          class="sb-icon-clipboard"
        />
      </slot>
    </span>
  </div>
</template>

<script>
import SbPopover from '@src/components/Popover/SbPopover.vue';
import FadeTransition from '@src/components/Transitions/Fade.vue';
import SbPanel from '@src/components/Panel/SbPanel.vue';
import SbText from '@src/components/Text/SbText.vue';
import { clipboard } from '@src/components/Clipboard/utils/directives/copyToClipboard';

class CopyMachine {
  constructor(A, S, S0, T) {
    this.A = A;
    this.S = S;
    this.S0 = S0;
    this.T = T;

    this.transitionCb = () => {};
  }

  get initialState() {
    return this.S0;
  }

  transition(state, input) {
    const newState = this.T[state][input] || state;
    this.transitionCb(newState);
    return newState;
  }
}

// INPUT ALPHABET
const A = {
  COPY: 'COPY',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
  RESET: 'RESET',
};
// SET OF FINITE STATES
const S = {
  copying: 'copying',
  idle: 'idle',
  success: 'success',
  error: 'error',
};
// INITIAL STATE
const S0 = S.idle;
// TRANSITION MAP
const T = {
  [S.idle]: {
    [A.COPY]: S.copying,
  },
  [S.copying]: {
    [A.SUCCESS]: S.success,
    [A.ERROR]: S.error,
  },
  [S.success]: {
    [A.COPY]: S.copying,
    [A.RESET]: S.idle,
  },
  [S.error]: {
    [A.COPY]: S.copying,
    [A.RESET]: S.idle,
  },
};
const machine = new CopyMachine(A, S, S0, T);

export default {
  name: 'SbClipboard',
  directives: { clipboard },
  components: {
    SbPanel,
    SbPopover,
    FadeTransition,
    SbText,
  },
  props: {
    copy: {
      type: String,
      required: true,
    },
    successMessage: {
      type: String,
      default: 'sbClipboard.successMessage',
    },
    errorMessage: {
      type: String,
      default: 'sbClipboard.errorMessage',
    },
    onSuccess: {
      type: Function,
      default: () => {},
      required: false,
    },
    onError: {
      type: Function,
      default: () => {},
      required: false,
    },
    resetTime: {
      type: Number,
      default: 2500,
    },
  },
  data: () => ({
    machine,
    state: machine.initialState,
    reset: null,
  }),
  methods: {
    invokeCopy() {
      this.state = this.machine.transition(this.state, A.COPY);
    },
    handleSuccess(val) {
      this.state = this.machine.transition(this.state, A.SUCCESS);
      this.handleReset();
      this.onSuccess(val);
    },
    handleError(val) {
      this.state = this.machine.transition(this.state, A.ERROR);
      this.handleReset();
      this.onError(val);
    },
    handleReset() {
      clearTimeout(this.reset);
      this.reset = setTimeout(() => {
        this.state = this.machine.transition(this.state, A.RESET);
      }, this.resetTime);
    },
  },
};
</script>

<style lang='scss'>
@import '@src/components/Clipboard/style/sb-clipboard.scss';
</style>
